/**
 * Pricing & Carnets Module — Gestion tarifs + augmentation annuelle + sync carnets + Power Kicker.
 * Hub admin pour piloter l'économie privé + progression élèves.
 */
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { DollarSign, TrendingUp, BookOpen, Sparkles, Loader2, RefreshCw, Users, Award, Video, Plus, Trash2, Youtube } from "lucide-react";

const CAD = (v) => new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD" }).format(v || 0);


export default function PricingCarnetsModule() {
  const [tab, setTab] = useState("rates");
  const [rates, setRates] = useState([]);
  const [increasePct, setIncreasePct] = useState(2.5);
  const [history, setHistory] = useState([]);
  const [carnetSummary, setCarnetSummary] = useState(null);
  const [pkData, setPkData] = useState(null);
  const [curriculum, setCurriculum] = useState([]);
  const [currDisc, setCurrDisc] = useState("");
  const [currAge, setCurrAge] = useState("");
  const [currStats, setCurrStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadRates = async () => {
    setLoading(true);
    try {
      const [r, h] = await Promise.all([api.get("/pricing/current-rates"), api.get("/pricing/history")]);
      setRates(r.data.current_rates);
      setHistory(h.data.history);
    } catch (e) { toast.error("Erreur tarifs"); }
    setLoading(false);
  };

  const loadCarnets = async () => {
    try {
      const r = await api.get("/carnets/by-discipline-summary");
      setCarnetSummary(r.data);
    } catch (e) { toast.error("Erreur carnets"); }
  };

  const loadPK = async () => {
    try {
      const r = await api.get("/power-kicker/members");
      setPkData(r.data);
    } catch (e) { toast.error("Erreur Power Kicker"); }
  };

  const loadCurriculum = async () => {
    setLoading(true);
    try {
      let r;
      if (currAge) {
        r = await api.get("/carnets/curriculum/by-age", { params: { age: parseInt(currAge, 10), discipline: currDisc || undefined } });
        setCurriculum(r.data.techniques);
      } else {
        r = await api.get("/carnets/curriculum/library", { params: { discipline: currDisc || undefined } });
        setCurriculum(r.data.techniques);
      }
      const s = await api.get("/carnets/curriculum/stats");
      setCurrStats(s.data);
    } catch (e) { toast.error("Erreur curriculum"); }
    setLoading(false);
  };

  const seedCurriculum = async () => {
    if (!window.confirm("Seeder le curriculum officiel (Judo, BJJ, Karaté, Muay Thai, Kickboxing, MMA) ? Idempotent, sans risque.")) return;
    try {
      const r = await api.post("/carnets/curriculum/seed");
      toast.success(`Curriculum seedé : ${r.data.created} créés, ${r.data.updated} mis à jour`);
      loadCurriculum();
    } catch (e) { toast.error("Erreur seed"); }
  };

  const addYoutubeLink = async (techId) => {
    const label = window.prompt("Label (Démo / Drill / Application / libre) :", "Démo");
    if (!label) return;
    const url = window.prompt("URL YouTube (https://youtu.be/... ou https://www.youtube.com/watch?v=...) :");
    if (!url) return;
    try {
      await api.post(`/carnets/curriculum/${techId}/youtube`, { label, url });
      toast.success("Lien ajouté");
      loadCurriculum();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur ajout"); }
  };

  const deleteYoutubeLink = async (techId, linkId) => {
    if (!window.confirm("Supprimer ce lien YouTube ?")) return;
    try {
      await api.delete(`/carnets/curriculum/${techId}/youtube/${linkId}`);
      toast.success("Supprimé");
      loadCurriculum();
    } catch (e) { toast.error("Erreur"); }
  };

  useEffect(() => {
    if (tab === "rates") loadRates();
    if (tab === "carnets") loadCarnets();
    if (tab === "pk") loadPK();
    if (tab === "curriculum") loadCurriculum();
  }, [tab, currDisc, currAge]);

  const syncCarnets = async () => {
    try {
      const r = await api.post("/carnets/sync-from-contracts");
      toast.success(`Carnets synchronisés : ${r.data.created} créés, ${r.data.updated} mis à jour`);
      loadCarnets();
    } catch (e) { toast.error("Erreur sync"); }
  };

  const normalizeBelts = async () => {
    if (!window.confirm("Corriger dans les données les libellés de ceinture en double et fusionner Can Ryu → Jujutsu Japonais ? (sans risque, relançable)")) return;
    try {
      const r = await api.post("/carnets/normalize-belts");
      toast.success(`✅ ${r.data.belts_fixed} ceinture(s) corrigée(s) · ${r.data.disciplines_merged} discipline(s) fusionnée(s)`);
      loadCarnets();
    } catch (e) { toast.error("Erreur nettoyage ceintures"); }
  };

  const applyIncrease = async () => {
    if (!window.confirm(`Augmenter TOUS les tarifs de +${increasePct}% maintenant ? Cette action est loggée et tracée mais difficile à reverser.`)) return;
    try {
      const r = await api.post("/pricing/apply-annual-increase", {
        pct: parseFloat(increasePct),
        apply_from: new Date().toISOString().slice(0, 10),
        notes: `Augmentation annuelle ${new Date().getFullYear()}`,
      });
      toast.success(`+${increasePct}% appliqué à ${r.data.updated_count} instructeurs`);
      loadRates();
    } catch (e) { toast.error("Erreur augmentation"); }
  };

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto" data-testid="pricing-carnets-module">
      <div>
        <h1 className="text-3xl font-bold text-amber-100 tracking-tight flex items-center gap-2">
          <DollarSign className="w-7 h-7 text-amber-400" /> Tarifs · Carnets · Power Kicker
        </h1>
        <p className="text-zinc-400 text-sm mt-1">Hub admin : grille tarifaire avec TPS/TVQ · augmentation annuelle · carnets de progression · membres Power Kicker</p>
      </div>

      <div className="flex gap-2 border-b border-zinc-800">
        {[
          { id: "rates", label: "💰 Tarifs", icon: DollarSign },
          { id: "carnets", label: "📒 Carnets progression", icon: BookOpen },
          { id: "curriculum", label: "🎬 Curriculum + YouTube", icon: Video },
          { id: "pk", label: "🥊 Power Kicker", icon: Sparkles },
        ].map(t => (
          <button
            key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
              tab === t.id ? "border-amber-500 text-amber-300" : "border-transparent text-zinc-400 hover:text-amber-200"
            }`}
            data-testid={`tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* TAB: Tarifs */}
      {tab === "rates" && (
        <div className="space-y-4">
          {loading && <Loader2 className="w-6 h-6 animate-spin text-amber-400 mx-auto" />}

          {/* Annual increase */}
          <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-r from-amber-500/5 to-zinc-950 p-5" data-testid="annual-increase-card">
            <h3 className="text-md font-semibold text-amber-200 flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4" /> Augmentation annuelle (coût de la vie)
            </h3>
            <p className="text-xs text-zinc-400 mb-3">Applique un % d'augmentation à TOUS les tarifs horaires des instructeurs. IPC Québec 2025-26 ≈ 2,5-3%.</p>
            <div className="flex items-center gap-2">
              <input
                type="number" step="0.1" min="0" max="20"
                value={increasePct}
                onChange={(e) => setIncreasePct(e.target.value)}
                className="w-24 bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-amber-100 text-sm"
                data-testid="increase-pct-input"
              />
              <span className="text-zinc-400 text-sm">% augmentation</span>
              <button
                onClick={applyIncrease}
                className="ml-auto px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold text-sm"
                data-testid="apply-increase-btn"
              >
                Appliquer maintenant
              </button>
            </div>
          </div>

          {/* Current grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {rates.map(r => (
              <div key={r.instructor_id} className="rounded-2xl border border-zinc-800 bg-zinc-950 p-4" data-testid={`rate-${r.instructor_id}`}>
                <div className="text-md font-semibold text-amber-100">{r.instructor_name}</div>
                <div className="text-xs text-zinc-400 mb-3">{CAD(r.rate_per_hour)}/heure (HT)</div>
                <div className="space-y-1.5 text-xs">
                  {r.packages.map(p => (
                    <div key={p.package} className="flex justify-between border-b border-zinc-800/40 pb-1">
                      <span className="text-zinc-300">{p.package}</span>
                      <span className="text-amber-300 font-mono">{CAD(p.total_ttc)} TTC</span>
                    </div>
                  ))}
                </div>
                <div className="text-[10px] text-zinc-500 mt-2 italic">+TPS 5% +TVQ 9,975%</div>
              </div>
            ))}
          </div>

          {/* History */}
          {history.length > 0 && (
            <div className="rounded-2xl border border-zinc-800 bg-zinc-950 p-4" data-testid="history-card">
              <h3 className="text-md font-semibold text-amber-200 mb-2">Historique augmentations</h3>
              <div className="space-y-1.5 text-xs">
                {history.slice(0, 10).map(h => (
                  <div key={h.id} className="flex justify-between border-b border-zinc-800/40 pb-1">
                    <span className="text-zinc-300">{h.applied_at.slice(0, 10)} · {h.instructor_name}</span>
                    <span className="text-amber-300 font-mono">{CAD(h.old_rate)} → {CAD(h.new_rate)} (+{h.pct_increase}%)</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB: Carnets */}
      {tab === "carnets" && (
        <div className="space-y-4">
          <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-r from-amber-500/5 to-zinc-950 p-5">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h3 className="text-md font-semibold text-amber-200 flex items-center gap-2">
                  <RefreshCw className="w-4 h-4" /> Synchronisation Carnets ↔ Contrats
                </h3>
                <p className="text-xs text-zinc-400 mt-1">
                  Crée/met à jour un carnet de progression pour chaque contrat actif avec ceinture définie.
                  Idempotent — peux le relancer après chaque ajustement ceinture.
                </p>
              </div>
              <button
                onClick={syncCarnets}
                className="px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold text-xs"
                data-testid="sync-carnets-btn"
              >
                Synchroniser maintenant
              </button>
              <button
                onClick={normalizeBelts}
                className="px-3 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-amber-300 border border-amber-700 font-semibold text-xs ml-2"
                data-testid="normalize-belts-btn"
              >
                🧹 Nettoyer les ceintures
              </button>
            </div>
          </div>

          {carnetSummary && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-zinc-800 bg-zinc-950 p-4">
                <div className="text-xs uppercase tracking-widest text-zinc-500">Total carnets</div>
                <div className="text-3xl font-bold text-amber-100 mt-1">{carnetSummary.total_carnets}</div>
                <div className="text-xs text-zinc-400 mt-1">élèves actifs avec ceinture assignée</div>
              </div>
              <div className="rounded-2xl border border-zinc-800 bg-zinc-950 p-4 space-y-2" data-testid="carnet-summary">
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Par discipline / ceinture</div>
                {Object.entries(carnetSummary.summary).map(([disc, belts]) => (
                  <div key={disc}>
                    <div className="text-amber-300 text-sm font-semibold">{(carnetSummary.labels && carnetSummary.labels[disc]) || disc.replace(/_/g, " ")}</div>
                    <div className="text-xs text-zinc-400 ml-3">
                      {Object.entries(belts).map(([b, n]) => `${b}: ${n}`).join(" · ")}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB: Power Kicker */}
      {tab === "pk" && (
        <div className="space-y-4">
          {pkData && (
            <>
              <div className="rounded-2xl border border-pink-500/30 bg-gradient-to-r from-pink-500/10 to-zinc-950 p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="pk-info-banner">
                <p className="text-xs text-pink-200">
                  🥊 <strong>Power Kicker</strong> — femmes 16 ans+ · discipline <strong>Kick-boxing / Muay-thaï</strong> · ceintures Blanche → Verte.
                  Chaque membre est relié à son abonnement, son statut de paiement et aux vidéos du carnet KB/MT.
                </p>
                <span className="px-3 py-1 rounded-full bg-red-500/20 text-red-200 text-xs flex items-center gap-1 whitespace-nowrap" data-testid="pk-total-videos">
                  <Video className="w-3.5 h-3.5" /> {pkData.total_videos || 0} vidéos KB/MT
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {Object.entries(pkData.by_level).map(([lvl, members]) => (
                  <div key={lvl} className="rounded-2xl border border-pink-500/30 bg-gradient-to-br from-pink-500/10 to-zinc-900 p-4" data-testid={`pk-level-${lvl}`}>
                    <div className="text-xs uppercase tracking-widest text-pink-300">{lvl}</div>
                    <div className="text-3xl font-bold text-pink-100 mt-1">{members.length}</div>
                    <div className="text-xs text-zinc-400">membres</div>
                  </div>
                ))}
              </div>

              <div className="rounded-2xl border border-zinc-800 bg-zinc-950 p-4">
                <h3 className="text-md font-semibold text-pink-200 mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" /> Membres Power Kicker ({pkData.total})
                </h3>
                {pkData.total === 0 && (
                  <div className="text-center text-zinc-500 py-6">
                    Aucune membre Power Kicker active. Vas dans <code className="text-amber-300">/app/contracts</code> pour activer la discipline.
                  </div>
                )}
                <div className="space-y-2">
                  {pkData.members.map((m) => (
                    <div key={m.id} className="flex items-center justify-between border-b border-zinc-800/50 pb-2 gap-3 flex-wrap" data-testid={`pk-member-${m.id}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-amber-100">{m.full_name}</div>
                        <div className="text-xs text-zinc-400">
                          {m.pk_level || "decouverte"} · {m.pk_attendance_count || 0} présences
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* Ceinture (Blanche → Verte) */}
                        <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-[10px] text-amber-200 flex items-center gap-1" data-testid={`pk-belt-${m.id}`}>
                          <Award className="w-3 h-3 text-pink-400" /> {m.pk_belt || "Blanche"}
                          {m.pk_next_belt && <span className="text-zinc-500">→ {m.pk_next_belt}</span>}
                        </span>
                        {/* Statut paiement / abonnement */}
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] ${m.payment?.en_retard ? "bg-rose-500/20 text-rose-300" : "bg-emerald-500/20 text-emerald-300"}`}
                          data-testid={`pk-payment-${m.id}`}
                        >
                          💳 {m.payment?.label || "—"}
                        </span>
                        {/* Vidéos / techniques rattachées (KB/MT) */}
                        <span className="px-2 py-0.5 rounded-full bg-red-500/20 text-[10px] text-red-200 flex items-center gap-1" data-testid={`pk-videos-${m.id}`}>
                          <Video className="w-3 h-3" /> {m.pk_techniques_for_belt || 0} tech · {m.pk_videos_for_belt || 0} vidéos
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* TAB: Curriculum (Terminologie par âge + YouTube) */}
      {tab === "curriculum" && (
        <div className="space-y-4">
          {/* Action bar */}
          <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-r from-amber-500/5 to-zinc-950 p-5 flex flex-wrap gap-3 items-end">
            <div>
              <div className="text-xs text-zinc-400 mb-1">Discipline</div>
              <select value={currDisc} onChange={(e) => setCurrDisc(e.target.value)}
                className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-amber-100"
                data-testid="curr-disc-select">
                <option value="">Toutes</option>
                <option value="judo">Judo</option>
                <option value="bjj">Jiu-Jitsu Brésilien</option>
                <option value="karate">Karaté</option>
                <option value="muay_thai">Muay Thai</option>
                <option value="kickboxing">Kickboxing</option>
                <option value="mma">MMA / Submission</option>
              </select>
            </div>
            <div>
              <div className="text-xs text-zinc-400 mb-1">Aperçu par âge (terminologie adaptée)</div>
              <select value={currAge} onChange={(e) => setCurrAge(e.target.value)}
                className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-amber-100"
                data-testid="curr-age-select">
                <option value="">Termes officiels (admin)</option>
                <option value="5">Petits Samouraïs 5 ans (noms fun)</option>
                <option value="7">Petits Samouraïs 7 ans (noms fun)</option>
                <option value="10">10 ans (FR + EN officiels)</option>
                <option value="16">16 ans (FR + EN officiels)</option>
                <option value="30">Adulte (FR + EN officiels)</option>
              </select>
            </div>
            <button onClick={seedCurriculum}
              className="ml-auto px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold text-xs"
              data-testid="seed-curriculum-btn">
              🌱 Seeder curriculum officiel
            </button>
          </div>

          {currStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-3">
                <div className="text-[10px] text-zinc-500 uppercase">Techniques</div>
                <div className="text-xl font-bold text-amber-100">{currStats.total_techniques}</div>
              </div>
              <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-3">
                <div className="text-[10px] text-zinc-500 uppercase">Vidéos YouTube</div>
                <div className="text-xl font-bold text-red-300">{currStats.total_youtube_videos}</div>
              </div>
              <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-3">
                <div className="text-[10px] text-zinc-500 uppercase">Couverture</div>
                <div className="text-xl font-bold text-emerald-300">{currStats.coverage_pct}%</div>
              </div>
              <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-3">
                <div className="text-[10px] text-zinc-500 uppercase">Disciplines</div>
                <div className="text-xl font-bold text-amber-100">{Object.keys(currStats.by_discipline || {}).length}</div>
              </div>
            </div>
          )}

          {loading && <Loader2 className="w-6 h-6 animate-spin text-amber-400 mx-auto" />}

          <div className="space-y-2">
            {curriculum.length === 0 && !loading && (
              <div className="text-center text-zinc-500 py-8">
                Aucune technique. Clique <strong>Seeder curriculum officiel</strong> pour démarrer.
              </div>
            )}
            {curriculum.map(t => (
              <div key={t.id} className="rounded-xl border border-zinc-800 bg-zinc-950 p-4" data-testid={`tech-${t.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="px-2 py-0.5 rounded-full bg-zinc-800 text-[10px] uppercase text-amber-300">{t.discipline}</span>
                      <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-[10px] text-amber-200">{t.belt}</span>
                      {t.category && <span className="text-[10px] text-zinc-500">{t.category}</span>}
                    </div>
                    {currAge ? (
                      <>
                        <div className={`text-md font-semibold mt-1.5 ${t.is_fun_terminology ? "text-pink-200" : "text-amber-100"}`}>
                          {t.display_primary}
                          {t.is_fun_terminology && <span className="text-[10px] text-pink-400 ml-2">(nom fun · Petits Samouraïs)</span>}
                        </div>
                        {t.display_secondary && (
                          <div className="text-xs text-zinc-400 italic">{t.display_secondary}</div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="text-md font-semibold text-amber-100 mt-1.5">{t.name_fr}</div>
                        <div className="text-xs text-zinc-400 italic">EN : {t.name_en}</div>
                        {t.fun_name && (
                          <div className="text-xs text-pink-300 mt-0.5">🌱 Petits Samouraïs : <em>{t.fun_name}</em></div>
                        )}
                      </>
                    )}
                  </div>
                  <button onClick={() => addYoutubeLink(t.id)}
                    className="px-3 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-red-200 text-xs flex items-center gap-1"
                    data-testid={`add-yt-${t.id}`}>
                    <Plus className="w-3 h-3" /> YouTube
                  </button>
                </div>
                {(t.youtube_links?.length > 0) && (
                  <div className="mt-3 space-y-1.5">
                    {t.youtube_links.map(yl => (
                      <div key={yl.id} className="flex items-center gap-2 text-xs bg-zinc-900/60 rounded-lg px-2 py-1.5 border border-zinc-800">
                        <Youtube className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
                        <span className="px-2 py-0.5 rounded-full bg-red-500/20 text-red-200 text-[10px]">{yl.label}</span>
                        <a href={yl.url} target="_blank" rel="noopener noreferrer"
                          className="text-blue-300 hover:underline truncate flex-1" data-testid={`yt-link-${yl.id}`}>
                          {yl.url}
                        </a>
                        <button onClick={() => deleteYoutubeLink(t.id, yl.id)} className="text-zinc-500 hover:text-rose-400" data-testid={`del-yt-${yl.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
